import React from 'react';

import { useFormikContext } from 'formik';

import * as Yup from 'yup';

import { StyledTextField } from 'components/Input/styles';

import UserAndGroupPicker, { isPickerUser, PickerUser } from 'components/SharedFormComponents/UserAndGroupPicker';

import { useStore } from 'utils/hooks/store';

import { MembershipMode, RuleCombinator } from 'utils/api/modules/memberGroups';

import { isDynamicGroup } from 'containers/MemberGroups/utils/membership';
import { RuleBuilder } from './RuleBuilder';
import { conditionHasValue, newCondition, RuleDraft, RULE_MAX_CONDITIONS } from './RuleBuilder/registry';
import { RulePreview } from './RulePreview';
import MembershipTypeCards from './MembershipTypeCards';
import { Field, FieldLabel, FormBody } from './MemberGroupForm.styles';
import { FieldError } from './RuleBuilder/styles';

export interface MemberGroupFormValues {
  name: string;
  description: string;
  membershipMode: MembershipMode;
  members: PickerUser[];
  rule: RuleDraft;
}

export const emptyRule = (): RuleDraft => ({ combinator: RuleCombinator.All, conditions: [newCondition()] });

export const memberGroupValidationSchema = Yup.object({
  name: Yup.string().trim().required('Name is required').max(256, 'Name must be 256 characters or fewer'),
  description: Yup.string().trim().max(1000, 'Description must be 1000 characters or fewer'),
  rule: Yup.object().when('membershipMode', {
    is: MembershipMode.Dynamic,
    then: (schema) =>
      schema.test('rule-valid', 'Give every condition a value before saving.', (value) => {
        const rule = value as RuleDraft | undefined;
        if (!rule || rule.conditions.length === 0 || rule.conditions.length > RULE_MAX_CONDITIONS) {
          return false;
        }
        return rule.conditions.every(conditionHasValue);
      }),
    otherwise: (schema) => schema.optional(),
  }),
});

interface MemberGroupFormProps {
  showTypeCards?: boolean;
  showMembers?: boolean;
  showRule?: boolean;
  ruleGroupUuid?: string;
  onTypeChange?: (mode: MembershipMode) => void;
}

const MemberGroupForm: React.FunctionComponent<MemberGroupFormProps> = ({
  showTypeCards = false,
  showMembers = false,
  showRule = false,
  ruleGroupUuid,
  onTypeChange,
}) => {
  const { values, errors, touched, submitCount, isValidating, handleChange, handleBlur, setFieldValue } =
    useFormikContext<MemberGroupFormValues>();
  const { notification } = useStore();

  const isDynamic = isDynamicGroup(values);
  const submitted = submitCount > 0;
  const showRuleSection = isDynamic && (showRule || showTypeCards);

  const toastedForSubmit = React.useRef(0);
  React.useEffect(() => {
    if (isValidating) {
      return;
    }
    if (submitCount > toastedForSubmit.current && Object.keys(errors).length > 0) {
      toastedForSubmit.current = submitCount;
      notification.enqueueErrorSnackbar('Please fix the highlighted fields before saving.');
    }
  }, [submitCount, isValidating, errors, notification]);

  return (
    <FormBody>
      <Field>
        <FieldLabel htmlFor="member-group-name">Name</FieldLabel>
        <StyledTextField
          id="member-group-name"
          name="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Name"
          error={touched.name && !!errors.name}
          helperText={touched.name && errors.name ? errors.name : undefined}
          fullWidth
        />
      </Field>

      <Field>
        <FieldLabel htmlFor="member-group-description">Description</FieldLabel>
        <StyledTextField
          id="member-group-description"
          name="description"
          value={values.description}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Description"
          error={touched.description && !!errors.description}
          helperText={touched.description && errors.description ? errors.description : undefined}
          multiline
          minRows={3}
          fullWidth
        />
      </Field>

      {showTypeCards && (
        <MembershipTypeCards
          value={values.membershipMode}
          onChange={(mode) => {
            setFieldValue('membershipMode', mode);
            onTypeChange?.(mode);
          }}
        />
      )}

      {showRuleSection && (
        <Field>
          <FieldLabel>Membership rule</FieldLabel>
          <RuleBuilder rule={values.rule} onChange={(rule) => setFieldValue('rule', rule)} submitted={submitted} />
          {submitted && typeof errors.rule === 'string' && <FieldError>{errors.rule}</FieldError>}
        </Field>
      )}

      {showRuleSection && (
        <Field>
          <RulePreview rule={values.rule} groupUuid={ruleGroupUuid} />
        </Field>
      )}

      {showMembers && !isDynamic && (
        <Field>
          <FieldLabel>Members</FieldLabel>
          <UserAndGroupPicker
            value={values.members}
            onChange={(next) => setFieldValue('members', next.filter(isPickerUser))}
            placeholder="Add users"
          />
        </Field>
      )}
    </FormBody>
  );
};

export default MemberGroupForm;
